<template>
    <teleport to="body">
        <div v-if="skill" class="tm-scope cd-backdrop" @click.self="$emit('close')">
            <aside class="cd" role="dialog" aria-modal="true" :aria-label="`Courses for ${skill.name}`" @keydown.esc="$emit('close')">
                <header class="cd__head">
                    <div class="cd__heading">
                        <h2 class="cd__title">Learn {{ skill.name }}</h2>
                        <span v-if="skill.count" class="cd__count">{{ skill.count }} {{ skill.count === 1 ? 'course' : 'courses' }}</span>
                    </div>
                    <button ref="closeBtn" type="button" class="cd__close" aria-label="Close" @click="$emit('close')">
                        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                    </button>
                </header>

                <div v-if="loading" class="cd__list">
                    <div v-for="n in 3" :key="n" class="cd__skeleton"></div>
                </div>
                <p v-else-if="!courses.length" class="cd__empty">No course teaches this skill yet.</p>
                <ul v-else class="cd__list">
                    <li v-for="c in courses" :key="c.course_id" class="cd__item">
                        <span class="cd__name">{{ c.title }}</span>
                        <span class="cd__meta">{{ meta(c) }}</span>
                        <span v-if="alsoCloses(c).length" class="cd__also">Also covers {{ alsoCloses(c).join(', ') }}</span>
                        <div class="cd__actions">
                            <button type="button" class="cd__btn cd__btn--go" @click="open(c)">Go to course</button>
                            <button type="button" class="cd__btn" :aria-pressed="saved.has(c.course_id)" @click="toggleSave(c)">
                                {{ saved.has(c.course_id) ? 'Saved' : 'Save' }}
                            </button>
                        </div>
                    </li>
                </ul>
            </aside>
        </div>
    </teleport>
</template>

<script>
import { nextTick, ref, watch } from 'vue'
import { CourseApiService } from '@/services/CourseApiService'

const PROVIDERS = { coursera: 'Coursera' }

// Courses for one missing skill, ranked by how much of the rest of the gap they also close.
// Shared by role details (Learn button) and the dashboard ("N courses").
export default {
    name: 'CourseDrawer',
    props: {
        skill: { type: Object, default: null },        // { id, name, count? } -- null = closed
        occupationId: { type: [Number, String], required: true },
        jobName: { type: String, default: '' },
        location: { type: String, default: 'Global' },
    },
    emits: ['close', 'saved'],
    setup(props, { emit }) {
        const courses = ref([])
        const loading = ref(false)
        const saved = ref(new Set())
        const closeBtn = ref(null)

        watch(() => props.skill, async (skill) => {
            if (!skill) return
            loading.value = true
            courses.value = []
            nextTick(() => closeBtn.value && closeBtn.value.focus())
            try {
                const res = await CourseApiService.ForSkill(skill.id, Number(props.occupationId), localStorage.getItem('cv_id'), props.location)
                courses.value = res.data.courses
            } catch (e) {
                courses.value = []
            } finally {
                loading.value = false
            }
        })

        const context = () => ({ occupation_id: Number(props.occupationId), skill_id: props.skill && props.skill.id })
        return {
            courses, loading, saved, closeBtn,
            meta: c => [c.institution, PROVIDERS[c.provider] || c.provider, c.workload]
                .filter((v, i, a) => v && a.indexOf(v) === i).join(' · '),
            // The other gap skills this course closes (the open skill is implied by the drawer).
            alsoCloses: c => c.fills.filter(f => f.skill_id !== props.skill.id).map(f => f.name),
            open: c => CourseApiService.Open(c, context()).then(() => emit('saved')),
            // Save again to undo.
            toggleSave: async c => {
                const on = !saved.value.has(c.course_id)
                await CourseApiService.UpdatePlan(c.course_id, on ? 'saved' : 'dropped', { occupation_id: Number(props.occupationId) })
                const next = new Set(saved.value)
                on ? next.add(c.course_id) : next.delete(c.course_id)
                saved.value = next
                emit('saved')
            },
        }
    },
}
</script>

<style scoped>
.cd-backdrop { position: fixed; inset: 0; z-index: 1050; background: rgba(11, 34, 57, 0.22); display: flex; justify-content: flex-end; }
.cd {
    width: min(420px, 100%); height: 100%; overflow-y: auto; box-sizing: border-box;
    background: var(--tm-surface); border-left: 1px solid var(--tm-border); box-shadow: -12px 0 32px rgba(11, 34, 57, 0.06);
    padding: 28px 28px 40px; display: flex; flex-direction: column; gap: 8px;
}
.cd__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 4px; }
.cd__heading { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.cd__title { margin: 0; font-family: var(--tm-font-display); font-size: 22px; line-height: 1.25; color: var(--tm-ink); }
.cd__title::first-letter { text-transform: uppercase; }
.cd__count { font-size: 13px; color: var(--tm-ink-muted); }
.cd__close {
    flex-shrink: 0; width: 32px; height: 32px; margin: -4px -6px 0 0; border-radius: 8px; border: 0;
    background: none; color: var(--tm-ink-muted); display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.cd__close:hover { background: var(--tm-surface-sunk); color: var(--tm-ink); }
.cd__empty { color: var(--tm-ink-muted); }
.cd__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
.cd__item { padding: 16px 0; border-bottom: 1px solid var(--tm-border-soft); display: flex; flex-direction: column; gap: 4px; }
.cd__item:last-child { border-bottom: 0; }
.cd__skeleton { height: 86px; margin: 12px 0; border-radius: var(--tm-radius-sm); background: var(--tm-surface-sunk); }
.cd__name { font-size: 14.5px; font-weight: 600; line-height: 1.35; color: var(--tm-ink); }
.cd__meta, .cd__also { font-size: 12.5px; color: var(--tm-ink-muted); }
.cd__also::first-letter { text-transform: uppercase; }
.cd__actions { display: flex; gap: 6px; margin-top: 8px; }
.cd__btn {
    height: 30px; padding: 0 12px; border-radius: 7px; border: 1px solid var(--tm-border); background: var(--tm-surface);
    font: inherit; font-size: 12.5px; font-weight: 600; color: var(--tm-ink-soft); cursor: pointer;
}
.cd__btn:hover { border-color: var(--tm-accent); color: var(--tm-accent-deep); }
.cd__btn[aria-pressed="true"] { background: var(--tm-accent-wash); border-color: transparent; color: var(--tm-accent-deep); }
.cd__btn--go { border: 0; background: var(--tm-accent); color: #FFFFFF; }
.cd__btn--go:hover { background: var(--tm-accent-deep); color: #FFFFFF; }
@media (max-width: 640px) { .cd { padding: 20px 16px 32px; } }
</style>
